(() => {
  const storageKey = 'carea-cart-checkout-window-15m-v2';

  const initializeTimer = (timer) => {
    if (timer.dataset.initialized === 'true') {
      return;
    }

    timer.dataset.initialized = 'true';
    const duration = Number(timer.dataset.duration || 900) * 1000;
    const value = timer.querySelector('[data-carea-timer-value]');
    const title = timer.querySelector('[data-carea-timer-title]');
    const note = timer.querySelector('[data-carea-timer-note]');
    const progress = timer.querySelector('[data-carea-timer-progress]');
    let endTime = Number(sessionStorage.getItem(storageKey));

    if (!endTime) {
      endTime = Date.now() + duration;
      sessionStorage.setItem(storageKey, String(endTime));
    }

    const update = () => {
      const remaining = Math.max(0, endTime - Date.now());
      const remainingRatio = Math.min(1, remaining / duration);

      if (progress) {
        progress.style.transform = `scaleX(${remainingRatio})`;
      }

      if (remaining === 0) {
        value.textContent = '00:00';
        title.textContent = 'Your cart is still saved';
        note.textContent = 'Stock and offer availability are confirmed at checkout.';
        return false;
      }

      const totalSeconds = Math.ceil(remaining / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      value.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
      return true;
    };

    if (!update()) {
      return;
    }

    const interval = window.setInterval(() => {
      if (!document.documentElement.contains(timer) || !update()) {
        window.clearInterval(interval);
      }
    }, 1000);
  };

  const initializeTimers = (root = document) => {
    root.querySelectorAll('[data-carea-cart-timer]').forEach(initializeTimer);
  };

  const animateCounter = (counter) => {
    const target = Number(counter.dataset.careaCount || 0);
    const decimals = Number(counter.dataset.careaDecimals || 0);
    const grouped = counter.dataset.careaGrouped === 'true';
    const startedAt = performance.now();
    const duration = 850;

    const renderValue = (value) => {
      const normalized = decimals > 0 ? value.toFixed(decimals) : String(Math.round(value));
      counter.textContent = grouped ? Number(normalized).toLocaleString('en-US') : normalized;
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      renderValue(target);
      return;
    }

    renderValue(0);

    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      renderValue(target * eased);

      if (progress < 1) {
        window.requestAnimationFrame(tick);
      }
    };

    window.requestAnimationFrame(tick);
  };

  const animateEvidenceCounters = (root) => {
    root.querySelectorAll('[data-carea-count]').forEach(animateCounter);
  };

  const activateEvidencePanel = (group, key) => {
    group.querySelectorAll('[data-carea-evidence-tab]').forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset.careaEvidenceTab === key));
    });

    group.querySelectorAll('[data-carea-evidence-panel]').forEach((panel) => {
      const isActive = panel.dataset.careaEvidencePanel === key;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;

      if (isActive && group.classList.contains('is-visible')) {
        animateEvidenceCounters(panel);
      }
    });
  };

  const evidenceObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add('is-visible');
          const activePanel = entry.target.querySelector('[data-carea-evidence-panel].is-active');

          if (activePanel) {
            animateEvidenceCounters(activePanel);
          }

          evidenceObserver.unobserve(entry.target);
        });
      }, { threshold: 0.22 })
    : null;

  const initializeEvidence = (root = document) => {
    const groups = [];

    if (root instanceof Element && root.matches('[data-carea-evidence]')) {
      groups.push(root);
    }

    groups.push(...root.querySelectorAll('[data-carea-evidence]'));

    groups.forEach((group) => {
      if (group.dataset.careaEvidenceReady === 'true') {
        return;
      }

      group.dataset.careaEvidenceReady = 'true';
      group.querySelectorAll('[data-carea-evidence-tab]').forEach((tab) => {
        tab.addEventListener('click', () => activateEvidencePanel(group, tab.dataset.careaEvidenceTab));
      });

      const tabs = Array.from(group.querySelectorAll('[data-carea-evidence-tab]'));
      const selectAdjacentTab = (direction) => {
        const selectedIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        const nextIndex = (selectedIndex + direction + tabs.length) % tabs.length;
        const nextTab = tabs[nextIndex];

        activateEvidencePanel(group, nextTab.dataset.careaEvidenceTab);
        nextTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      };

      group.querySelector('[data-carea-evidence-prev]')?.addEventListener('click', () => selectAdjacentTab(-1));
      group.querySelector('[data-carea-evidence-next]')?.addEventListener('click', () => selectAdjacentTab(1));

      if (evidenceObserver) {
        evidenceObserver.observe(group);
      } else {
        group.classList.add('is-visible');
      }
    });
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }

        if (node.matches('[data-carea-cart-timer]')) {
          initializeTimer(node);
        }

        initializeTimers(node);
        initializeEvidence(node);
      });
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    initializeTimers();
    initializeEvidence();
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
